'use client';

import React from 'react';
import styled from 'styled-components';

/**
 * IncidentMetricCard Component
 * 
 * Displays key metrics for incident details (STATUS, ROOT CAUSE, DURATION)
 * Used in the incident detail page to show incident information in a card layout
 */

export interface IncidentMetricCardProps {
  /** Card label (e.g., "STATUS", "ROOT CAUSE", "DURATION") */
  label: string;
  /** Main value to display */
  value: string | React.ReactNode;
  /** Optional subtitle/description */
  subtitle?: string;
  /** Optional icon element */
  icon?: React.ReactNode;
  /** Visual variant */
  variant?: 'default' | 'success' | 'warning' | 'error';
  /** Optional badge to display (e.g., status code) */
  badge?: {
    text: string;
    variant: 'success' | 'warning' | 'error';
  };
  className?: string;
}

// ============================================================================
// Styled Components
// ============================================================================

const Card = styled.div`
  background: #ffffff;
  border: 1px solid #e5e7eb;
  border-radius: 8px;
  padding: 24px;
  display: flex;
  flex-direction: column;
  gap: 12px;
  flex: 1;
  min-width: 0;
`;

const Label = styled.div`
  font-size: 12px;
  font-weight: 600;
  color: #6b7280;
  text-transform: uppercase;
  letter-spacing: 0.05em;
`;

const ContentRow = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
`;

const IconWrapper = styled.div<{ $variant: string }>`
  width: 48px;
  height: 48px;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
  background-color: ${({ $variant }) => {
    switch ($variant) {
      case 'success':
        return '#10b981';
      case 'warning':
        return '#f59e0b';
      case 'error':
        return '#ef4444';
      default:
        return '#6b7280';
    }
  }};

  svg {
    width: 24px;
    height: 24px;
    color: white;
  }
`;

const ValueContainer = styled.div`
  flex: 1;
  min-width: 0;
`;

const Value = styled.div<{ $variant: string }>`
  font-size: 32px;
  font-weight: 700;
  color: ${({ $variant }) => {
    switch ($variant) {
      case 'success':
        return '#10b981';
      case 'warning':
        return '#f59e0b';
      case 'error':
        return '#ef4444';
      default:
        return '#111827';
    }
  }};
  line-height: 1.2;
  word-break: break-word;
`;

const BadgeWrapper = styled.span<{ $variant: 'success' | 'warning' | 'error' }>`
  display: inline-block;
  background: ${({ $variant }) => {
    switch ($variant) {
      case 'success':
        return '#10b981';
      case 'warning':
        return '#f59e0b';
      case 'error':
        return '#ef4444';
    }
  }};
  color: white;
  padding: 6px 12px;
  border-radius: 6px;
  font-size: 14px;
  font-weight: 600;
  margin-left: 12px;
`;

const Subtitle = styled.div`
  font-size: 14px;
  color: #6b7280;
  line-height: 1.5;
  word-break: break-word;
`;

// ============================================================================
// Component
// ============================================================================

export const IncidentMetricCard: React.FC<IncidentMetricCardProps> = ({
  label,
  value,
  subtitle,
  icon,
  variant = 'default',
  badge,
  className,
}) => {
  return (
    <Card className={className}>
      <Label>{label}</Label>
      <ContentRow>
        {icon && <IconWrapper $variant={variant}>{icon}</IconWrapper>}
        <ValueContainer>
          <Value $variant={variant}>
            {value}
            {badge && <BadgeWrapper $variant={badge.variant}>{badge.text}</BadgeWrapper>}
          </Value>
        </ValueContainer>
      </ContentRow>
      {subtitle && <Subtitle>{subtitle}</Subtitle>}
    </Card>
  );
};

export default IncidentMetricCard;





